import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ReferenceLine } from 'recharts';
import './MttVarianceCalculatorPage.css';

const MTTVarianceCalculator = () => {
  // Default tournament type
  const defaultTournamentType = {
    id: 1,
    players: 100,
    placesPaid: 5,
    buyIn: 50,
    rake: 10,
    roi: 15,
    count: 200
  };

  // State for tournament types, bankroll, sample size, and results
  const [tournamentTypes, setTournamentTypes] = useState([defaultTournamentType]);
  const [bankroll, setBankroll] = useState('');
  const [sampleSize, setSampleSize] = useState(1000);
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationTime, setCalculationTime] = useState(0);

  // Payout structure for 5 places paid (37%, 25%, 15%, 12%, 11%)
  const payoutStructure5 = [0.37, 0.25, 0.15, 0.12, 0.11];

  // Handle input changes for tournament types
  const handleTournamentTypeChange = (index, field, value) => {
    const updatedTypes = [...tournamentTypes];
    updatedTypes[index] = {
      ...updatedTypes[index],
      [field]: field === 'placesPaid' ? parseInt(value) : Number(value)
    };
    setTournamentTypes(updatedTypes);
  };

  // Add a new tournament type
  const addTournamentType = () => {
    const newId = Math.max(...tournamentTypes.map(t => t.id), 0) + 1;
    setTournamentTypes([...tournamentTypes, { ...defaultTournamentType, id: newId }]);
  };

  // Remove a tournament type
  const removeTournamentType = (index) => {
    if (tournamentTypes.length > 1) {
      const updatedTypes = [...tournamentTypes];
      updatedTypes.splice(index, 1);
      setTournamentTypes(updatedTypes);
    }
  };

  // Run simulation and calculate variance
  const calculateVariance = () => {
    setIsCalculating(true);
    const startTime = performance.now();
    
    // Process inputs
    const processedTypes = tournamentTypes.map(type => {
      const netBuyIn = type.buyIn * (1 - type.rake / 100);
      const prizePool = netBuyIn * type.players;
      
      // Create payout structure based on places paid
      let payouts;
      if (type.placesPaid === 5) {
        payouts = payoutStructure5.map(percentage => percentage * prizePool);
      } else {
        // Default to 5 places paid for now, will expand later
        payouts = payoutStructure5.map(percentage => percentage * prizePool);
      }
      
      return {
        ...type,
        netBuyIn,
        prizePool,
        payouts
      };
    });

    // Run simulations
    const allResults = runSimulations(processedTypes, sampleSize, bankroll ? Number(bankroll) : null);
    setResults(allResults);
    
    const endTime = performance.now();
    setCalculationTime(Math.round(endTime - startTime));
    setIsCalculating(false);
  };

  // Main simulation function
  const runSimulations = (tournamentTypes, sampleSize, initialBankroll) => {
    const totalTournaments = tournamentTypes.reduce((sum, type) => sum + type.count, 0);
    const combinedSamples = [];
    const probabilityData = [];
    const randomSamples = [];
    
    // Generate random samples
    for (let i = 0; i < sampleSize; i++) {
      const sample = simulateTournamentRun(tournamentTypes, initialBankroll);
      combinedSamples.push(sample);
      
      // Select 20 random samples for display
      if (i < 20) {
        randomSamples.push(sample);
      }
    }
    
    // Calculate percentile data
    for (let percentile = 0.5; percentile <= 100; percentile += 0.5) {
      const index = Math.floor((percentile / 100) * sampleSize) - 1;
      const sortedResults = [...combinedSamples].map(s => s.finalProfit).sort((a, b) => a - b);
      probabilityData.push({
        probability: percentile,
        net: Math.round(sortedResults[index])
      });
    }
    
    // Calculate statistics
    const ev = Math.round(combinedSamples.reduce((sum, s) => sum + s.finalProfit, 0) / sampleSize);
    const buyinsPlusFees = getTotalBuyins(tournamentTypes);
    const theoreticalEv = calculateTheoreticalEv(tournamentTypes);
    const sd = calculateStandardDeviation(combinedSamples.map(s => s.finalProfit), ev);
    
    // Find best and worst runs
    const bestRun = [...combinedSamples].sort((a, b) => b.finalProfit - a.finalProfit)[0];
    const worstRun = [...combinedSamples].sort((a, b) => a.finalProfit - b.finalProfit)[0];
    
    // Calculate probability of loss
    const lossCount = combinedSamples.filter(s => s.finalProfit < 0).length;
    const probabilityOfLoss = (lossCount / sampleSize) * 100;
    
    return {
      probabilityData,
      randomSamples: generateRandomSampleData(randomSamples, totalTournaments),
      bestRun,
      worstRun,
      stats: {
        totalTournaments,
        buyinsPlusFees,
        ev,
        theoreticalEv,
        roi: ((ev / buyinsPlusFees) * 100).toFixed(0),
        theoreticalRoi: ((theoreticalEv / buyinsPlusFees) * 100).toFixed(0),
        sd,
        sampleSize,
        probabilityOfLoss
      }
    };
  };

  // Simulate a single tournament run
  const simulateTournamentRun = (tournamentTypes, initialBankroll) => {
    let profit = 0;
    let bankrollHistory = [initialBankroll || 0];
    let currentBankroll = initialBankroll;
    const tournamentResults = [];
    
    let tournamentNumber = 0;
    
    // Process each tournament type
    for (const type of tournamentTypes) {
      for (let i = 0; i < type.count; i++) {
        tournamentNumber++;
        // Simulate tournament result based on ROI
        const result = simulateTournamentResult(type);
        profit += result;
        
        // Track bankroll if provided
        if (initialBankroll !== null) {
          currentBankroll += result;
          bankrollHistory.push(currentBankroll);
          
          // Check for ruin
          if (currentBankroll < 0) {
            break;
          }
        }
        
        tournamentResults.push({
          type: type.id,
          tournament: tournamentNumber,
          result,
          cumulativeProfit: profit
        });
      }
    }
    
    return {
      finalProfit: profit,
      bankrollHistory,
      tournamentResults,
      ruined: currentBankroll !== null && currentBankroll < 0
    };
  };

  // Simulate result of a single tournament
  const simulateTournamentResult = (tournamentType) => {
    const { buyIn, netBuyIn, payouts, roi } = tournamentType;
    
    // Calculate finish position based on ROI
    const random = Math.random();
    
    // The total prize pool
    const totalPrizePool = payouts.reduce((sum, payout) => sum + payout, 0);
    
    // Calculate the probability of cashing that would yield the desired ROI
    // Expected value = (probability of cashing * average payout when cashing) - buy-in
    // For ROI% = (EV / buy-in) * 100
    // Therefore: probability of cashing = ((ROI/100) * buyIn + buyIn) / average payout when cashing
    
    const averageCashPayout = totalPrizePool / payouts.length;
    const targetEV = buyIn * (roi / 100);
    const probCash = (targetEV + buyIn) / averageCashPayout;
    
    let cumulativeProbability = 0;
    
    // For each possible payment position
    for (let i = 0; i < payouts.length; i++) {
      // Each position has equal probability within the cash positions
      const positionProb = probCash / payouts.length;
      cumulativeProbability += positionProb;
      
      if (random < cumulativeProbability) {
        return payouts[i] - buyIn; // Return profit (payout minus buy-in)
      }
    }
    
    // If you don't cash, you lose your buy-in
    return -buyIn;
  };

  // Generate data for random sample display
  const generateRandomSampleData = (samples, totalTournaments) => {
    const sampleData = [];
    
    // Create data points for each tournament
    for (let i = 0; i < totalTournaments; i++) {
      const dataPoint = {
        tournament: i + 1
      };
      
      // Add each sample's value at this tournament
      samples.forEach((sample, sampleIndex) => {
        if (i < sample.tournamentResults.length) {
          dataPoint[`sample${sampleIndex + 1}`] = sample.tournamentResults[i].cumulativeProfit;
        } else {
          // If sample doesn't have result for this tournament (due to ruin), use last value
          dataPoint[`sample${sampleIndex + 1}`] = sample.tournamentResults[sample.tournamentResults.length - 1].cumulativeProfit;
        }
      });
      
      // Add best and worst runs if available
      if (samples.length > 0) {
        let bestValue = -Infinity;
        let worstValue = Infinity;
        
        samples.forEach((sample, index) => {
          if (dataPoint[`sample${index + 1}`] > bestValue) {
            bestValue = dataPoint[`sample${index + 1}`];
          }
          if (dataPoint[`sample${index + 1}`] < worstValue) {
            worstValue = dataPoint[`sample${index + 1}`];
          }
        });
        
        dataPoint.best = bestValue;
        dataPoint.worst = worstValue;
      }
      
      sampleData.push(dataPoint);
    }
    
    return sampleData;
  };

  // Helper functions
  const getTotalBuyins = (types) => {
    return types.reduce((sum, type) => sum + (type.buyIn * type.count), 0);
  };

  const calculateTheoreticalEv = (types) => {
    return types.reduce((sum, type) => {
      return sum + (type.buyIn * type.count * type.roi / 100);
    }, 0);
  };

  const calculateStandardDeviation = (values, mean) => {
    const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / values.length;
    return Math.round(Math.sqrt(variance));
  };

  // Get the payout structure display for a tournament type
  const getPayoutStructureDisplay = (type) => {
    const netBuyIn = type.buyIn * (1 - type.rake / 100);
    const prizePool = netBuyIn * type.players;
    
    if (type.placesPaid === 5) {
      return payoutStructure5.map((percentage, index) => {
        const payout = Math.round(percentage * prizePool);
        return (
          <div key={index} className="payout-row">
            <span>Place {index + 1}:</span>
            <span>${payout}</span>
          </div>
        );
      });
    }
    
    // Default to showing 5 places
    return payoutStructure5.map((percentage, index) => {
      const payout = Math.round(percentage * prizePool);
      return (
        <div key={index} className="payout-row">
          <span>Place {index + 1}:</span>
          <span>${payout}</span>
        </div>
      );
    });
  };

  return (
    <div className="mtt-variance-calculator">
      <h1>MTT Variance Calculator</h1>
      
      <div className="calculator-inputs">
        <h2>Enter tournament data</h2>
        
        {tournamentTypes.map((type, index) => (
          <div key={type.id} className="tournament-type">
            <h3>Tournament #{index + 1}</h3>
            
            <div className="input-group">
              <label>
                # of players
                <input
                  type="number"
                  min="2"
                  value={type.players}
                  onChange={(e) => handleTournamentTypeChange(index, 'players', e.target.value)}
                />
              </label>
              
              <label>
                Places paid
                <select
                  value={type.placesPaid}
                  onChange={(e) => handleTournamentTypeChange(index, 'placesPaid', e.target.value)}
                >
                  <option value="5">5 places paid</option>
                </select>
              </label>
            </div>
            
            <div className="input-group">
              <label>
                Buy-in in USD
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={type.buyIn}
                  onChange={(e) => handleTournamentTypeChange(index, 'buyIn', e.target.value)}
                />
              </label>
              
              <label>
                Rake in %
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={type.rake}
                  onChange={(e) => handleTournamentTypeChange(index, 'rake', e.target.value)}
                />
              </label>
            </div>
            
            <div className="input-group">
              <label>
                ROI in %
                <input
                  type="number"
                  value={type.roi}
                  onChange={(e) => handleTournamentTypeChange(index, 'roi', e.target.value)}
                />
              </label>
              
              <label>
                How many are you going to play?
                <input
                  type="number"
                  min="1"
                  value={type.count}
                  onChange={(e) => handleTournamentTypeChange(index, 'count', e.target.value)}
                />
              </label>
            </div>
            
            <div className="payout-structure">
              <h4>Payouts for a ${type.buyIn} tourney with {type.players} players</h4>
              <div className="payout-table">
                {getPayoutStructureDisplay(type)}
              </div>
            </div>
            
            {tournamentTypes.length > 1 && (
              <button 
                className="remove-button" 
                onClick={() => removeTournamentType(index)}
              >
                Remove tournament
              </button>
            )}
          </div>
        ))}
        
        <button className="add-button" onClick={addTournamentType}>
          Add another tournament type
        </button>
        
        <div className="global-inputs">
          <div className="input-group">
            <label>
              Sample size - How many times should the tourney setup above be simulated?
              <input
                type="number"
                min="100"
                max="10000"
                value={sampleSize}
                onChange={(e) => setSampleSize(Number(e.target.value))}
              />
            </label>
          </div>
          
          <div className="input-group">
            <label>
              Bankroll (In USD) - What's your starting bankroll? (can be left empty)
              <input
                type="number"
                min="0"
                value={bankroll}
                onChange={(e) => setBankroll(e.target.value)}
                placeholder="Optional"
              />
            </label>
          </div>
        </div>
        
        <button 
          className="calculate-button" 
          onClick={calculateVariance}
          disabled={isCalculating}
        >
          {isCalculating ? 'Calculating...' : 'Calculate'}
        </button>
        
        {calculationTime > 0 && (
          <div className="calculation-time">
            Calculation time: {calculationTime}ms
          </div>
        )}
      </div>
      
      {results && (
        <div className="calculator-results">
          <h2>Results</h2>
          
          <div className="results-section">
            <h3>Combined distribution</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={results.probabilityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="probability" 
                    label={{ value: 'Percentile', position: 'insideBottomRight', offset: -10 }} 
                  />
                  <YAxis 
                    label={{ value: 'Net Profit ($)', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value}`, 'Net']}
                    labelFormatter={(value) => `Percentile: ${value}%`}
                  />
                  <ReferenceLine y={0} stroke="#000" />
                  <Area type="monotone" dataKey="net" stroke="#8884d8" fill="#8884d8" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            <div className="probability-table">
              <h4>Probability Table</h4>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Percentile</th>
                      <th>Net Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.probabilityData
                      .filter((item, index) => index % 10 === 0) // Show every 5th percentile
                      .map((item) => (
                        <tr key={item.probability}>
                          <td>{item.probability}%</td>
                          <td>${item.net}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <div className="results-section">
            <h3>20 Random samples</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={results.randomSamples}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="tournament" 
                    label={{ value: 'Tournaments', position: 'insideBottomRight', offset: -10 }} 
                  />
                  <YAxis 
                    label={{ value: 'Net Profit ($)', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value}`, 'Net']}
                    labelFormatter={(value) => `Tournament: ${value}`}
                  />
                  <ReferenceLine y={0} stroke="#000" />
                  <Legend />
                  {Array.from({ length: 20 }, (_, i) => (
                    <Line 
                      key={i} 
                      type="monotone" 
                      dataKey={`sample${i + 1}`} 
                      stroke={`hsl(${i * 18}, 70%, 50%)`} 
                      dot={false} 
                      name={`Sample ${i + 1}`}
                    />
                  ))}
                  <Line 
                    type="monotone" 
                    dataKey="best" 
                    stroke="#00c853" 
                    strokeWidth={2} 
                    dot={false} 
                    name="Best run" 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="worst" 
                    stroke="#d50000" 
                    strokeWidth={2} 
                    dot={false} 
                    name="Worst run" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="results-section stats-section">
            <h3>Statistical Data</h3>
            <div className="stats-table">
              <table>
                <tbody>
                  <tr>
                    <td>Tournament types</td>
                    <td>{tournamentTypes.length}</td>
                  </tr>
                  <tr>
                    <td>Total tournaments played</td>
                    <td>{results.stats.totalTournaments}</td>
                  </tr>
                  <tr>
                    <td>Sample size</td>
                    <td>{results.stats.sampleSize}</td>
                  </tr>
                  <tr>
                    <td>Buy-ins + fees</td>
                    <td>${results.stats.buyinsPlusFees}</td>
                  </tr>
                  <tr>
                    <td>Expected Value (theoretical)</td>
                    <td>${results.stats.theoreticalEv}</td>
                  </tr>
                  <tr>
                    <td>Average net profit (simulated)</td>
                    <td>${results.stats.ev}</td>
                  </tr>
                  <tr>
                    <td>Average ROI (simulated)</td>
                    <td>{results.stats.roi}%</td>
                  </tr>
                  <tr>
                    <td>Standard deviation</td>
                    <td>${results.stats.sd}</td>
                  </tr>
                  <tr>
                    <td>Probability of loss (simulated)</td>
                    <td>{results.stats.probabilityOfLoss.toFixed(1)}% ({Math.round(results.stats.sampleSize * results.stats.probabilityOfLoss / 100)} out of {results.stats.sampleSize} samples)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MTTVarianceCalculator;